import { Component, OnInit } from '@angular/core';
import { TableModule } from 'primeng/table';
import { SidebarModule } from 'primeng/sidebar';
import { FloorPlan } from 'src/app/core/shared/interface';
import { FloorPlanFormComponent } from '../floor-plan-form/floor-plan-form.component';
import { ButtonModule } from 'primeng/button';
import { PropertyService } from '../../properties/service/property.service';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { FormsModule } from '@angular/forms';
import { CalendarModule } from 'primeng/calendar';
import { SliderModule } from 'primeng/slider';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { WaitlistService } from '../../../core/shared/waitlist.service';

@Component({
  selector: 'app-floor-plan-listing',
  standalone: true,
  imports: [ToastModule, TableModule, SidebarModule, FloorPlanFormComponent, ButtonModule, CommonModule, DialogModule, DropdownModule, FormsModule, CalendarModule, SliderModule],
  templateUrl: './floor-plan-listing.component.html',
  styleUrl: './floor-plan-listing.component.scss',
  providers: [MessageService],
})
export class FloorPlanListingComponent implements OnInit {
  type: string = '';
  propertyId = "6724ec85459e3cdf8db55149";
  display: boolean = false;
  position: 'center' | 'right' = 'center';
  visible: boolean = false;
  blockSpecialCharsPattern = /[.*+?^${}()|[\]\\]/g;
  floorPlans: FloorPlan[] = [];
  totalRecords: number = 0;
  rowsPerPage: number = 10;
  currentPage: number = 0;
  filters: any = {
    unit: '',
    beds: '',
    baths: '',
    sq_ft: '',
    //max_sq_ft: '',
    search: '',
    waitlist: "all",
  };
  sorting: any = {
    field: 'unit',
    order: 1
  };
  isSidebarVisible: boolean = false;
  selectedFloorPlan: FloorPlan = { property_id: this.propertyId } as FloorPlan;

  allPlanNames: { label: any; value: any; }[] = [];
  allBedOptions: { label: any; value: any; }[] = [];
  allBathOptions: { label: any; value: any; }[] = [];
  allSqftOptions: { label: any; value: any; }[] = [];
  waitlistOptions = [
    { label: "All Floorplans", value: "all" },
    { label: "Available Floorplans", value: false },
    { label: "Waitlist Floorplans", value: true }
  ];
  isWaitListSidebarVisible: boolean = false;
  waitlistUsers: any[] = [];
  minSqFtOptions: any[] = [];
  maxSqFtOptions: any[] = [];
  constructor(
    private propertyService: PropertyService, 
    private messageService: MessageService,
    private waitlistService: WaitlistService,
  ) { }

  ngOnInit(): void {
    //this.fetchAllFloorPlans();
    this.loadFilterOptions();
  }

  showSuccess(message: string) {
    this.messageService.add({ severity: 'success', summary: 'Success', detail: message });
  }

  showError(message: string) {
    this.messageService.add({ severity: 'error', summary: 'Error', detail: message });
  }

  fetchAllFloorPlans(): void {
    this.propertyService.getAllFloorPlans(this.propertyId, {}).subscribe({
      next: (response: { data: FloorPlan[]; total: number; }) => {
        const floorPlans = response.data || response;
        // Save floor plans for pagination
        // this.floorPlans = floorPlans;
        // this.totalRecords = response.total;
      },
      error: () => {
        console.error('Error fetching floor plans');
      }
    });
  }

  private buildApiFilters() {
    const filters = { ...this.filters };

    // Extract only min/max from sqft dropdown
    if (filters.sq_ft?.value) {
      filters.sq_ft = filters.sq_ft.value;
    } else {
      delete filters.sq_ft;
    }

    return filters;
  }

  getAllFloorPlans(event: any): void {

    const page = Math.floor(event.first / event.rows) + 1;
    const limit = event.rows;

    const queryParams = {
      page,
      limit,
      sortField: event.sortField || 'unit',
      sortOrder: event.sortOrder || 1,
      filters: JSON.stringify(this.buildApiFilters())
    };

    this.propertyService.getAllFloorPlans(this.propertyId, queryParams).subscribe({
      next: (response: { data: FloorPlan[]; total: number; }) => {
        this.floorPlans = response.data || response;
        this.totalRecords = response.total;
        this.currentPage = page - 1;
      },
      error: () => {
        console.error('Error fetching floor plans');
      }
    });
  }

  loadFilterOptions(): void {
    this.propertyService.getFloorPlanFiltersOnly(this.propertyId).subscribe((response: any) => {
      this.allBedOptions = this.mapToDropdown<number>(response.beds);
      this.allBathOptions = this.mapToDropdown<number>(response.baths);
      //this.allSqftOptions = this.mapToDropdown<number>(response.sq_ft);

      const sqFtValues = response.sq_ft; // backend should send all exact values sorted
      if (sqFtValues?.length) {
        this.allSqftOptions = this.buildSqFtRangesFromValues(sqFtValues);
      }
    });
  }

  private buildSqFtRangesFromValues(values: number[]) {
    const ranges:any = [];

    if (!values.length) return ranges;

    let start = values[0];

    for (let i = 1; i < values.length; i++) {
      const end = values[i];
      ranges.push({
        label: `${start} – ${end} sq ft`,
        value: { min: start, max: end }
      });
      start = end; // next range starts after current end
    }

    // Last range
    ranges.push({
      label: `${start}+ sq ft`,
      value: { min: start }
    });

    return ranges;
  }

  private mapToDropdown<T>(arr: T[]): { label: T; value: T }[] {
    return arr.filter(v => v !== undefined && v !== null && v !== '').map(v => ({ label: v, value: v }));
  }

  onFilterChange(): void {
    this.getAllFloorPlans({
      first: this.currentPage * this.rowsPerPage,
      rows: this.rowsPerPage,
      sortField: this.sorting.field,
      sortOrder: this.sorting.order
    });
  }

  resetFilters(): void {
    this.filters = {
      unit: '',
      beds: '',
      baths: '',
      sq_ft: '',
      //max_sq_ft: '',
      search: '',
      waitlist: "all"
    };
    this.onFilterChange();
  }

  openCreateSidebar(): void {
    this.selectedFloorPlan = { property_id: this.propertyId } as FloorPlan;
    this.isSidebarVisible = true;
    this.type = 'create';
  }

  editFloorPlan(floorPlan: FloorPlan): void {
    this.selectedFloorPlan = { ...floorPlan };
    this.isSidebarVisible = true;
    this.type = 'edit';
  }

  onFormSubmit(floorPlanFormData: FormData): void {
    if (this.selectedFloorPlan._id) {
      this.propertyService.updateFloorPlan(this.selectedFloorPlan._id, floorPlanFormData).subscribe(() => {
        this.showSuccess('Floor Plan updated successfully');
        this.getAllFloorPlans({ first: this.currentPage * this.rowsPerPage, rows: this.rowsPerPage });
        this.loadFilterOptions();
      });
    } else {
      this.propertyService.createFloorPlan(floorPlanFormData).subscribe(() => {
        this.showSuccess('Floor Plan Created successfully');
        this.getAllFloorPlans({ first: this.currentPage * this.rowsPerPage, rows: this.rowsPerPage });
        this.loadFilterOptions();
      });
    }
    this.isSidebarVisible = false;
  }

  confirmDelete(floorPlan: any): void {
    this.selectedFloorPlan = floorPlan;
    this.display = true;
  }

  onCancel(): void {
    this.display = false;
    this.selectedFloorPlan = {} as FloorPlan;
  }

  onConfirm(): void {
    if (this.selectedFloorPlan) {
      this.propertyService.deleteFloorPlan(this.selectedFloorPlan._id).subscribe(() => {
        this.showSuccess('Flore Plan deleted successfully!');
        this.getAllFloorPlans({ first: this.currentPage * this.rowsPerPage, rows: this.rowsPerPage });
        this.display = false;
        this.selectedFloorPlan = {} as FloorPlan;
      });
    }
  }

  // Block on typing
  blockSpecialChars(event: KeyboardEvent) {
    console.log("BLOCK typing");
    if (this.blockSpecialCharsPattern.test(event.key)) {
      event.preventDefault();
    }
  }

  // Block on paste
  blockSpecialCharsOnPaste(event: ClipboardEvent) {
    console.log("BLOCK PASTE");
    const pasteData = event.clipboardData?.getData('text') || '';
    if (this.blockSpecialCharsPattern.test(pasteData)) {
      event.preventDefault();
    }
  }

  // Block on drop
  blockSpecialCharsOnDrop(event: DragEvent) {
    console.log("BLOCK Drop");
    const dropData = event.dataTransfer?.getData('text') || '';
    if (this.blockSpecialCharsPattern.test(dropData)) {
      event.preventDefault();
    }
  }

  // Final cleanup in case something sneaks in
  sanitizeInput(event: Event) {
    console.log("SANITIZE INPUT");
    const input = event.target as HTMLInputElement;
    if (this.blockSpecialCharsPattern.test(input.value)) {
      input.value = input.value.replace(this.blockSpecialCharsPattern, '');
    }
  }

  refreshFloorPlans(): void {
    this.propertyService.getAllFloorPlans(this.propertyId, {}).subscribe({
      next: (response: { data: FloorPlan[]; total: number; }) => {
        const floorPlans = response.data || response;
        this.floorPlans = floorPlans;

        // this.allPlanNames = [...new Set(floorPlans.map(fp => fp.unit))].map(u => ({ label: u, value: u }));
        // this.allBedOptions = [...new Set(floorPlans.map(fp => fp.beds))].map(b => ({ label: b, value: b }));
        // this.allBathOptions = [...new Set(floorPlans.map(fp => fp.baths))].map(b => ({ label: b, value: b }));
        // this.allSqftOptions = [...new Set(floorPlans.map(fp => fp.sq_ft))].map(s => ({ label: s, value: s }));
        
        // this.allBathOptions.sort((a, b) => a.value - b.value);
        // this.allBedOptions.sort((a, b) => a.value - b.value);
        // this.allSqftOptions.sort((a, b) => a.value - b.value);
      },
      error: () => {
        console.error('Error fetching floor plans');
      }
    });
  }
  onWaitlistClick(unit: any) {
    this.waitlistService.getWaitlistByTarget('unit', unit._id).subscribe(
      (res) => {
        this.waitlistUsers = res;
        this.isWaitListSidebarVisible = true;
      },
      (err) => console.error(err)
    );
  }
  closeSidebar() {
    this.isWaitListSidebarVisible = false;
    this.waitlistUsers = [];
    this.selectedFloorPlan = {} as FloorPlan;
  }
}